import { useQuery } from "@tanstack/react-query";
import { useReducedMotion } from "motion/react";
import { createContext, useContext, useMemo } from "react";
import { orpc } from "@/utils/orpc";

type AnimationContextType = {
	/** Whether to disable decorative animations (CMS disabled OR user prefers reduced motion) */
	shouldReduceMotion: boolean;
	/** Raw CMS setting - whether admin enabled animations */
	animationsEnabled: boolean;
	/** User's system preference for reduced motion */
	prefersReducedMotion: boolean;
};

const AnimationContext = createContext<AnimationContextType | undefined>(
	undefined,
);

/**
 * Animation Provider
 *
 * Combines CMS feature flag (enableAnimations) with user's prefers-reduced-motion.
 * Decorative animations are disabled when EITHER:
 * - Admin disables them in CMS
 * - User has prefers-reduced-motion enabled
 */
export function AnimationProvider({ children }: { children: React.ReactNode }) {
	// Fetch feature flags - orpc now works correctly with SYNC .server()
	const { data: featureFlags } = useQuery(orpc.getFeatureFlags.queryOptions());

	// useReducedMotion returns null on server, false/true on client
	const prefersReducedMotion = useReducedMotion() ?? false;

	const value = useMemo<AnimationContextType>(() => {
		// Default to true if not loaded yet (show animations optimistically)
		const animationsEnabled = featureFlags?.enableAnimations ?? true;

		return {
			// Disable animations if CMS disabled OR user prefers reduced
			shouldReduceMotion: !animationsEnabled || prefersReducedMotion,
			animationsEnabled,
			prefersReducedMotion,
		};
	}, [featureFlags?.enableAnimations, prefersReducedMotion]);

	return (
		<AnimationContext.Provider value={value}>
			{children}
		</AnimationContext.Provider>
	);
}

/**
 * Hook to access animation settings
 *
 * Returns whether animations should be reduced (CMS disabled OR user preference)
 */
export function useAnimationSettings() {
	const context = useContext(AnimationContext);
	if (context === undefined) {
		throw new Error(
			"useAnimationSettings must be used within an AnimationProvider",
		);
	}
	return context;
}
